import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { FloatingPortal } from '@floating-ui/react';
import { useLongPress, useMediaQuery } from '@uidotdev/usehooks';
import { Laugh, Reply } from 'lucide-react';
import Skeleton from 'react-loading-skeleton';

import { isImageContentType } from '../../lib/attachments';
import { BREAKPOINT_SM } from '../../lib/breakpoints';
import { useMediaAttachments } from '../../lib/media_attachments_store';
import { currentShadowId } from '../../lib/meta';
import { FAILED_ATTACHMENT_STATUS, FAILED_STATUS, getUploadIds, resolveOptimisticSortOrder } from '../../lib/optimistic_messages';
import { requestMessageJump } from '../../lib/pendingMessageJump';
import { cn } from '../../lib/utils';

import EditAsteriskIcon from '../../../assets/images/edit-asterisk.svg';
import ReadMoreDotsIcon from '../../../assets/images/read-more-three-dots.svg';
import ReplyArrowIcon from '../../../assets/images/reply-arrow.svg';

import { KEYCHAIN } from '../../contexts/KeyMaster';
import { useRouteWorkspace } from '../../contexts/RouteWorkspaceContext';
import { getMessageBubbleMaxWidthClassName } from '../../feed/echo/messageBubbleGeometry';
import { observeMessageContentOverflow } from '../../feed/echo/messageContentOverflow';
import useHuggedBubbleWidth from '../../hooks/useHuggedBubbleWidth';
import useMessageReactionEmojibar from '../../hooks/useMessageReactionEmojibar';
import useResolvedProfile from '../../hooks/useResolvedProfile';
import EmojibarPopover from '../EmojibarPopover';
import ProfilePopoverContent from '../ProfilePopoverContent';
import ContextMenuPortal from '../portals/ContextMenuPortal';
import EntityPopover from '../shared/EntityPopover';
import ProfileTag from '../shared/ProfileTag';
import { Tooltip } from '../shared/Tooltip';
import LinkPreviewCard from './LinkPreviewCard';
import LocalTime from './LocalTime';
import MessageAttachments from './MessageAttachments';
import MessageAvatar from './MessageAvatar';
import RichdownContent from './RichdownContent';
import TapbackReaction from './TapbackReaction';
import UnreadMarker from './UnreadMarker';
import { getMessageBubbleSurfaceClassName } from './messageStyleClasses';
import { getMessagePlaceholderSeed, seededSkeletonWidth } from './skeletonSeed';

export const SHORT_TIME_THRESHOLD = 60 * 60 * 24; // 24 hours

// Exported alongside the content class so rows that mirror the message anatomy
// off-file (ConferenceTranscript) share the same recipes instead of copying them.
export const MESSAGE_CONTENT_TYPOGRAPHY_CLASS = 'font-sans text-[15px]/[20px] font-normal tracking-[-0.01em] not-italic';
export const AUTHOR_NAME_TYPOGRAPHY_CLASS = 'message-author-name font-sans text-[13px]/[20px] font-semibold not-italic';
export const MESSAGE_TIMESTAMP_TYPOGRAPHY_CLASS =
  'message-timestamp font-sans text-[11px]/[20px] font-normal not-italic lining-nums proportional-nums';

const isPlaceholderPseudotype = (pseudotype) => pseudotype === 'skeleton';

const MessageHeader = ({ message, author, interactive = true }) => {
  const isProfileAuthor = message.author_type === 'profile';
  const resolvedAuthor = useResolvedProfile(author, interactive && isProfileAuthor);

  // Clicking a name shows the profile popover (via EntityPopover's clickEnabled) — never a DM
  // or account settings — so swallow the click before it reaches an interactive ancestor.
  const handleAuthorClick = useCallback((event) => {
    event?.stopPropagation();
  }, []);
  // One per-row seed keeps this header skeleton width stable while it's rendered.
  const placeholderSeed = getMessagePlaceholderSeed(message);

  const popoverContent = isProfileAuthor && resolvedAuthor?.id ? <ProfilePopoverContent profile={resolvedAuthor} /> : null;

  const optimisticSortOrder = resolveOptimisticSortOrder(message);
  const canShowProfileTag = isProfileAuthor && message.pseudotype !== 'optimistic' && resolvedAuthor?.tag;

  const handleRetryMessage = useCallback(() => {
    if (!optimisticSortOrder) return;

    window.dispatchEvent(
      new CustomEvent('output:internal:live-journal:submit-message', {
        bubbles: true,
        detail: {
          messageData: {
            text: message.text || '',
            channel_id: message.channel_id,
            workspace_id: message.workspace_id,
            attachments: Array.isArray(message.attachments) ? message.attachments : [],
            reply_to_message_id: message.reply_to_message_id || null,
            optimistic_sort_order: optimisticSortOrder,
            retry_mode: 'manual',
          },
        },
      }),
    );
  }, [message, optimisticSortOrder]);

  const handleRetryAttachments = useCallback(() => {
    if (!optimisticSortOrder) return;

    window.dispatchEvent(
      new CustomEvent('output:internal:live-journal:retry-optimistic-message-attachments', {
        bubbles: true,
        detail: { optimisticSortOrder },
      }),
    );
  }, [optimisticSortOrder]);

  return (
    <div className="ml-12 flex items-center pb-1">
      <div className="flex w-full items-center">
        <div className="flex min-w-0">
          {isPlaceholderPseudotype(message.pseudotype) || message.author_pseudotype === 'skeleton' ? (
            <div>
              <Skeleton
                count={1}
                width={seededSkeletonWidth({ seed: placeholderSeed, min: 150, max: 200 })}
                height={12}
                borderRadius="16px"
              />
            </div>
          ) : (
            <div className="flex min-w-0 items-center gap-[6px] text-cursed md:text-3xs">
              {/* Author */}
              {isProfileAuthor ? (
                <EntityPopover content={popoverContent} interactive={interactive} clickEnabled={interactive}>
                  <button
                    tabIndex={interactive ? null : -1}
                    onClick={handleAuthorClick}
                    data-app-region={interactive ? 'none' : 'drag'}
                    className={cn('inline-block truncate', AUTHOR_NAME_TYPOGRAPHY_CLASS, interactive && 'cursor-pointer')}
                  >
                    <span className="select-none">{resolvedAuthor?.full_name || 'Unknown'}</span>
                  </button>
                </EntityPopover>
              ) : (
                <span className={cn('inline-block truncate select-none', AUTHOR_NAME_TYPOGRAPHY_CLASS)}>
                  {author?.full_name || 'Unknown'}
                </span>
              )}

              {/* Profile tag, between the author name and timestamp */}
              {canShowProfileTag && <ProfileTag tag={resolvedAuthor.tag} hex={resolvedAuthor.tag_color} className="shrink-0 select-none" />}

              {/* Timestamp (not shown for optimistic messages) */}
              {message.pseudotype !== 'optimistic' && (
                <LocalTime
                  timestamp={message.timestamp}
                  format={Date.now() / 1000 - message.timestamp > SHORT_TIME_THRESHOLD ? '%-m/%-d/%y · %-I:%M %p' : '%-I:%M %p'}
                  className={cn('select-none', MESSAGE_TIMESTAMP_TYPOGRAPHY_CLASS)}
                />
              )}

              {message.pseudotype === 'optimistic' && message.status === FAILED_ATTACHMENT_STATUS && (
                <>
                  <span className="font-semibold text-red-500 select-none">Attachment failed</span>
                  <button
                    onClick={handleRetryAttachments}
                    data-app-region="none"
                    className="cursor-pointer font-medium text-gray-500 underline select-none hover:text-gray-700"
                  >
                    Retry attachments
                  </button>
                </>
              )}

              {/* Failed message retry UI */}
              {message.pseudotype === 'optimistic' && message.status === FAILED_STATUS && (
                <>
                  <span className="font-semibold text-red-500 select-none">Failed to send message</span>
                  <button
                    onClick={handleRetryMessage}
                    data-app-region="none"
                    className="cursor-pointer font-medium text-gray-500 underline select-none hover:text-gray-700"
                  >
                    Try Again
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ReplyAuthorRow = ({ name }) => (
  <div className="flex items-center gap-2 text-pink-500">
    <ReplyArrowIcon className="size-4.5 shrink-0" />
    <span className="font-semibold">{name}</span>
  </div>
);

// h-[22px] so the loading placeholder has the same known height as entity lines (profile/channel flexicons)
const ReplyContentWrapper = ({ children }) => (
  <div className="richdown-content--unformatted non-interactive-entities line-clamp-1 h-[22px] !text-cursed/5">{children}</div>
);

const ReplyPlaceholderText = ({ children }) => (
  <div className="richdown-content--base">
    <span className="line-clamp-1 h-[22px] leading-none text-gray-500 italic opacity-50">{children}</span>
  </div>
);

const ReplyQuote = ({ replyToMessage, replyToAuthor, replyToEntitySnapshots, isReplyToMissing }) => {
  if (!replyToMessage) {
    return (
      <>
        <ReplyAuthorRow name={isReplyToMissing ? 'Unknown' : 'Loading...'} />
        <ReplyContentWrapper>
          <ReplyPlaceholderText>{isReplyToMissing ? 'Original message not found' : 'loading...'}</ReplyPlaceholderText>
        </ReplyContentWrapper>
      </>
    );
  }

  // bugbot: || 1 treats 0 as falsy, use ?? to handle empty arrays correctly
  const attachments = replyToMessage.attachments ?? [];
  const attachmentCount = attachments.length;
  const imageThumbs = attachments
    .filter((attachment) => isImageContentType(attachment.metadata?.content_type))
    .map((attachment) => ({
      id: attachment.id,
      url: attachment.variants?.thumb?.public_url || attachment.variants?.medium?.public_url || null,
      fileName: attachment.metadata?.file_name,
    }))
    .filter((thumb) => thumb.url);
  const hasImageThumbs = imageThumbs.length > 0;

  return (
    <>
      <ReplyAuthorRow name={replyToAuthor?.full_name || 'Unknown'} />
      {hasImageThumbs && (
        <div className="flex flex-wrap gap-1.5">
          {imageThumbs.map((thumb) => (
            <img
              key={thumb.id}
              src={thumb.url}
              alt={thumb.fileName || 'Image'}
              className="size-14 rounded-lg object-cover"
              draggable={false}
            />
          ))}
        </div>
      )}
      {!replyToMessage.contains_only_attachments ? (
        <ReplyContentWrapper>
          <RichdownContent html={replyToMessage.text} entitySnapshots={replyToEntitySnapshots} />
        </ReplyContentWrapper>
      ) : !hasImageThumbs ? (
      <ReplyContentWrapper>
          <ReplyPlaceholderText>
            {attachmentCount} attachment{attachmentCount !== 1 && 's'}
          </ReplyPlaceholderText>
      </ReplyContentWrapper>
      ) : null}
    </>
  );
};

const Message = ({
  message,
  author,
  debugEnabled = false,
  interactive = true,
  standalone = false,
  isGroupedWithPrevious = false,
  isGroupedWithNext = false,
  shouldShowUnreadMarker = false,
  onUnreadMarkerDismiss,
  unreadMarkerWorkspaceId,
  unreadMarkerProfileId,
  onOpenFullMessage,
  onOpenImageLightbox,
}) => {
  const { profileId: currentProfileId } = useRouteWorkspace();
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
  const [clickedAttachment, setClickedAttachment] = useState(null);
  const [isContentOverflowing, setIsContentOverflowing] = useState(false);
  const [isContextMenuEmojibarOpen, setIsContextMenuEmojibarOpen] = useState(false);
  const emojibarKeychainId = isContextMenuEmojibarOpen
    ? KEYCHAIN.POPOVER__MESSAGE_REACTION_CONTEXT_MENU_EMOJIBAR
    : KEYCHAIN.POPOVER__MESSAGE_REACTION_EMOJIBAR;

  const {
    isOpen: isEmojibarOpen,
    setIsOpen: setIsEmojibarOpen,
    toggle: toggleEmojibar,
    refs: emojibarRefs,
    floatingStyles: emojibarFloatingStyles,
    contentRef: emojibarContentRef,
    handleEmojiSelect,
    update: updateEmojibarPosition,
    setOffset: setEmojibarOffset,
  } = useMessageReactionEmojibar(message.workspace_id, message.channel_id, message.id, { keychainId: emojibarKeychainId });

  // Opening the Add Emoji modal (from the picker's bottom bar) dismisses the reaction picker.
  useEffect(() => {
    const closeEmojibarForAddEmojiModal = () => setIsEmojibarOpen(false);
    window.addEventListener('output:internal:add-emoji-modal', closeEmojibarForAddEmojiModal);
    return () => window.removeEventListener('output:internal:add-emoji-modal', closeEmojibarForAddEmojiModal);
  }, [setIsEmojibarOpen]);

  const messageRef = useRef(null);
  const bubbleRef = useRef(null);
  const contentRef = useRef(null);

  // Reply data comes pre-attached by the feed's reply resolution (replyToMessage) -
  // no Dexie lookup needed here, it's already enriched with resource and entity snapshots
  const replyToMessage = message.replyToMessage;
  const isReplyToMissing = message.reply_to_message_id && message.replyToStatus === 'failed';
  const replyToAuthor = replyToMessage?.author;
  const replyToEntitySnapshots = replyToMessage?.entity_snapshots;
  const optimisticUploadIds = useMemo(() => getUploadIds(message), [message]);
  const optimisticUploadsMap = useMediaAttachments(optimisticUploadIds);
  const displayedAttachments = useMemo(() => {
    const snapshotAttachments = Array.isArray(message.attachments) ? message.attachments : [];
    if (message.pseudotype !== 'optimistic' || optimisticUploadIds.length === 0) return snapshotAttachments;

    // Prefer live Dexie upload rows for progress/errors, fall back to the message snapshot.
    // This keeps attachment UI updating under the optimistic bubble after the composer moves on.
    const fallbackByUploadId = new Map();
    snapshotAttachments.forEach((attachment) => {
      if (!attachment?.id) return;
      fallbackByUploadId.set(attachment.id, attachment);
    });

    return optimisticUploadIds
      .map((uploadId) => optimisticUploadsMap.get(uploadId) || fallbackByUploadId.get(uploadId) || null)
      .filter(Boolean);
  }, [message.attachments, message.pseudotype, optimisticUploadIds, optimisticUploadsMap]);
  const visibleLinkPreviews = useMemo(
    () => (message.link_previews || []).filter((preview) => preview && preview.dismissed !== true),
    [message.link_previews],
  );
  const hasMultipleDisplayedAttachments = displayedAttachments.length > 1;

  // overrides - must come before any code that uses `interactive`
  if (isPlaceholderPseudotype(message.pseudotype)) {
    interactive = false;
    isGroupedWithPrevious = false;
    isGroupedWithNext = false;
  }

  if (message.pseudotype === 'optimistic') {
    interactive = false;

    if (message.status === FAILED_STATUS || message.status === FAILED_ATTACHMENT_STATUS) {
      isGroupedWithPrevious = false;
      isGroupedWithNext = false;
    }
  }

  // Click the reply quote to jump to the original message
  const canJumpToReply = interactive && !!replyToMessage?.sort_order;
  const handleJumpToReply = useCallback(
    (event) => {
      if (!canJumpToReply) return;
      event.stopPropagation();
      requestMessageJump({ channelId: message.channel_id, sortOrder: replyToMessage.sort_order });
    },
    [canJumpToReply, message.channel_id, replyToMessage?.sort_order],
  );

  const isBreakpointSm = useMediaQuery(BREAKPOINT_SM);

  // shadows can see reactions but cannot create them
  const canCreateReactions = !currentShadowId();

  // Convert reactions object to array for processing
  // The feed's renderer-row enrichment attaches `emoji` and `profile` snapshots so
  // rendering can rely on local data without additional Dexie lookups.
  const reactions = useMemo(() => Object.values(message.reactions || {}), [message.reactions]);

  // Split up reactions into stickers and tapbacks, pre-sorted by sort_order
  // Note: We're not using Dexie/useLiveQuery - emoji and profile data would need to come from API or context
  const stickers = useMemo(() => {
    return reactions
      .filter((reaction) => reaction.pos_x !== null && reaction.pos_y !== null)
      .sort((a, b) => a.sort_order.localeCompare(b.sort_order))
      .map((reaction) => ({
        ...reaction,
        emoji: reaction.emoji || null,
        profile: reaction.profile || null,
        isOwnReaction: reaction.profile_id === currentProfileId,
      }));
  }, [reactions, currentProfileId]);

  const tapbacks = useMemo(() => {
    return reactions
      .filter((reaction) => reaction.pos_x === null && reaction.pos_y === null)
      .sort((a, b) => a.sort_order.localeCompare(b.sort_order))
      .map((reaction) => ({
        ...reaction,
        emoji: reaction.emoji || null,
        profile: reaction.profile || null,
        isOwnReaction: reaction.profile_id === currentProfileId,
      }));
  }, [reactions, currentProfileId]);

  const hasTapbacks = tapbacks.length > 0;
  const tapbackCount = tapbacks.length;
  const previousTapbackCountRef = useRef(tapbackCount);
  const tapbackRevealTimeoutRef = useRef(null);
  const [isTapbackRowRevealReady, setIsTapbackRowRevealReady] = useState(true);
  const isFirstTapbackEntering = previousTapbackCountRef.current === 0 && hasTapbacks;
  const shouldHideTapbackRow = isFirstTapbackEntering || !isTapbackRowRevealReady;

  useEffect(() => {
    const previousTapbackCount = previousTapbackCountRef.current;
    previousTapbackCountRef.current = tapbackCount;

    // Delay the appearance of the first tapback to avoid jittery effects while the row settles.
    if (previousTapbackCount === 0 && tapbackCount > 0) {
      setIsTapbackRowRevealReady(false);
      tapbackRevealTimeoutRef.current = window.setTimeout(() => {
        setIsTapbackRowRevealReady(true);
        tapbackRevealTimeoutRef.current = null;
      }, 50);
    } else if (tapbackCount === 0) {
      if (tapbackRevealTimeoutRef.current) {
        window.clearTimeout(tapbackRevealTimeoutRef.current);
        tapbackRevealTimeoutRef.current = null;
      }
      setIsTapbackRowRevealReady(true);
    }
  }, [tapbackCount]);

  useEffect(() => {
    return () => {
      if (tapbackRevealTimeoutRef.current) {
        window.clearTimeout(tapbackRevealTimeoutRef.current);
      }
    };
  }, []);

  // Long press on mobile
  const longPressAttrs = useLongPress(
    (e) => {
      if (!isBreakpointSm) return;
      if (!interactive) return;

      e.preventDefault();
      e.stopPropagation();

      let x = 0;
      let y = 0;

      if (['touchstart', 'touchmove', 'touchend', 'touchcancel'].includes(e.type)) {
        const touch = e.touches[0] || e.changedTouches[0];
        if (touch) {
          x = touch.pageX;
          y = touch.pageY;
        }
      } else if (['mousedown', 'mouseup', 'mousemove', 'mouseover', 'mouseout', 'mouseenter', 'mouseleave'].includes(e.type)) {
        x = e.clientX;
        y = e.clientY;
      }

      setContextMenuPosition({ x, y });
      setShowContextMenu(true);
    },
    {
      threshold: 1000,
    },
  );

  // Context menu on non-mobile
  const handleContextMenu = useCallback(
    (e) => {
      if (isBreakpointSm) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      if (!interactive) return;

      // don't show context menu if text is selected, meta/alt keys are held, or on links
      const selection = window.getSelection();
      if (selection && selection.toString()) return;
      if (e.metaKey || e.altKey) return;
      if (e.target.tagName === 'A' || e.target.closest('a')) return;

      // cancel the standard context menu
      e.preventDefault();
      e.stopPropagation();

      // dismiss any existing context menus first
      window.dispatchEvent(new CustomEvent('output:toggle-context-menu', { detail: { isOpen: false } }));

      // detect if clicking on an image and find its attachment by data-media-attachment-id
      const imgElement = e.target.tagName === 'IMG' ? e.target : e.target.closest('img');
      let attachment = null;
      if (imgElement) {
        const id = imgElement.dataset?.mediaAttachmentId;
        attachment = id ? (message.attachments?.find((a) => a.id === id) ?? null) : null;
      }
      setClickedAttachment(attachment);

      // use global coordinates for the context menu portal
      setContextMenuPosition({ x: e.clientX, y: e.clientY });
      setShowContextMenu(true);
    },
    [message, setContextMenuPosition, setShowContextMenu, isBreakpointSm, interactive],
  );

  const handleCloseContextMenu = useCallback(() => {
    setShowContextMenu(false);
  }, [setShowContextMenu]);

  const handleOpenReactionPicker = useCallback(
    (position) => {
      setIsContextMenuEmojibarOpen(true);

      // If position is provided (from context menu), create a virtual reference element
      if (position) {
        setEmojibarOffset(0); // No offset when opening at click position
        emojibarRefs.setReference({
          getBoundingClientRect() {
            return {
              width: 0,
              height: 0,
              x: position.x,
              y: position.y,
              left: position.x,
              right: position.x,
              top: position.y,
              bottom: position.y,
            };
          },
        });
        // Virtual elements need manual update() call since ResizeObserver can't detect changes
        requestAnimationFrame(() => updateEmojibarPosition?.());
      } else {
        setEmojibarOffset(20); // Default offset when opening from button
      }
      setIsEmojibarOpen(true);
    },
    [setIsEmojibarOpen, emojibarRefs, updateEmojibarPosition, setEmojibarOffset],
  );

  // Anchors to the clicked button. Two add-reaction buttons can be mounted at once
  // (hover rail + tapback row), so a render-time `ref={setReference}` on both would
  // let whichever mounted last win — anchor at click time instead.
  const handleToggleEmojibar = useCallback(
    (event) => {
      setIsContextMenuEmojibarOpen(false);
      if (event?.currentTarget) emojibarRefs.setReference(event.currentTarget);
      toggleEmojibar();
    },
    [toggleEmojibar, emojibarRefs],
  );

  // Carry the rendered row so the reply banner can name the author without a
  // legacy-store lookup that feed-only messages cannot satisfy.
  const handleReplyClick = useCallback(() => {
    window.dispatchEvent(
      new CustomEvent('output:reply-to-message', { detail: { messageId: message.id, channelId: message.channel_id, message } }),
    );
  }, [message]);

  // Once the picker closes, clear the context-menu-open origin flag.
  useEffect(() => {
    if (!isEmojibarOpen && isContextMenuEmojibarOpen) {
      setIsContextMenuEmojibarOpen(false);
    }
  }, [isEmojibarOpen, isContextMenuEmojibarOpen]);

  const handleReactionClick = useCallback(
    async (reaction, event, { forceRemove = false } = {}) => {
      if (reaction.profile_id === currentProfileId && (forceRemove || event.metaKey)) {
        // Your own reaction is removed by ⌘-click or the popover's Remove; a plain
        // click stacks another copy instead (the duplicate branch below).
        // dispatch event to live-journal controller to handle both optimistic and real reactions
        window.dispatchEvent(
          new CustomEvent('output:internal:live-journal:remove-reaction', {
            bubbles: true,
            detail: {
              channel_id: message.channel_id,
              workspace_id: message.workspace_id,
              message_id: message.id,
              reaction_id: reaction.id,
            },
          }),
        );
      } else {
        // Plain clicks duplicate — your own (stack in place) or someone else's (join in)
        // dispatch event to live-journal controller to handle optimistic reaction creation and submission
        window.dispatchEvent(
          new CustomEvent('output:internal:live-journal:submit-reaction', {
            bubbles: true,
            detail: {
              reactionData: {
                channel_id: message.channel_id,
                workspace_id: message.workspace_id,
                message_id: message.id,
                emoji_id: reaction.emoji_id,
                pos_x: reaction.pos_x,
                pos_y: reaction.pos_y,
                scale: reaction.scale,
                angle: reaction.angle,
              },
            },
          }),
        );
      }
    },
    [currentProfileId, message.channel_id, message.id, message.workspace_id],
  );

  // Pass the rendered row itself: the modal must not depend on re-finding this
  // message in any store after the virtualized source row unmounts.
  const handleOpenFullMessageClick = useCallback(() => {
    onOpenFullMessage?.(message);
  }, [message, onOpenFullMessage]);

  const handleTapbackAddReactionClick = useCallback(
    (e) => {
      e.stopPropagation();
      handleToggleEmojibar(e);
    },
    [handleToggleEmojibar],
  );

  // The reaction picker renders in a portal, but React still bubbles its events
  // through this row — a right-click inside the picker must not also open the
  // message context menu behind it.
  const handleEmojibarContextMenu = useCallback((e) => {
    e.stopPropagation();
  }, []);

  const isPlaceholder = isPlaceholderPseudotype(message.pseudotype);
  // Reuse the same seed for all skeleton pieces in this row so they feel coherent.
  const placeholderSeed = getMessagePlaceholderSeed(message);
  // Own-ness must not wait on the resolved author snapshot: rows can render
  // before entity snapshots load, and a late flip animates the whole
  // mine/not-mine bubble restyle.
  const isOwnMessage = message.author_type === 'profile' && String(message.author_id) === currentProfileId;
  const isEditedMessage = message.flags?.includes('edited');
  const renderLinkPreview = useCallback(
    (preview) => <LinkPreviewCard key={preview.id} preview={preview} isOwnMessage={isOwnMessage} />,
    [isOwnMessage],
  );

  // Message content bubble classes
  const bubbleClassName = useMemo(() => {
    const classes = ['richdown-bubble--base', 'message-content-collapsed'];

    if (isContentOverflowing) classes.push('message-content-overflowing');

    // contains-only-emoji-entities gets same special treatment regardless of who posted it
    if (message.contains_only_emoji_entities) {
      classes.push('richdown-bubble--channel-message-contains-only-emoji-entities');
    } else {
      classes.push(getMessageBubbleSurfaceClassName(isOwnMessage));
      classes.push(getMessageBubbleMaxWidthClassName());
    }

    if (message.reply_to_message_id) {
      classes.push('richdown-bubble--base--with-reply');
    }

    return classes.join(' ');
  }, [isContentOverflowing, isOwnMessage, message.contains_only_emoji_entities, message.reply_to_message_id]);

  useLayoutEffect(() => {
    if (message.contains_only_attachments) {
      setIsContentOverflowing(false);
      return undefined;
    }
    if (!bubbleRef.current || !contentRef.current) return undefined;

    return observeMessageContentOverflow(bubbleRef.current, contentRef.current, setIsContentOverflowing);
  }, [message.contains_only_attachments, message.id]);

  // Hug the bubble to its longest rendered line; reply bubbles keep their
  // natural width since the quoted card stretches to fill them.
  useHuggedBubbleWidth({
    bubbleRef,
    contentRef,
    enabled: !isPlaceholder && !message.contains_only_attachments && !message.contains_only_emoji_entities && !message.reply_to_message_id,
    contentKey: message.text,
  });

  // The attachment scroller bleeds to the panel edges, so a cluster parked beside it would
  // land on top of the images. Those rows ride it inside the scroller as the last item,
  // right after the final tile; every other message keeps it alongside the bubble.
  const stacksHoverCluster = hasMultipleDisplayedAttachments;
  // Shown for every message, image bubbles included (their old floating reaction chip is
  // gone). Messages with tapbacks show this too — redundant with the tapback row's
  // add-reaction button on purpose, so the hover affordance is consistent everywhere.
  const hoverCluster = canCreateReactions ? (
    <div
      // will-change pins the wrapper to its own compositing layer permanently, so the
      // fade's layer promotion/collapse can't re-rasterize the icon at a different
      // half-pixel (the hover "scoot" on odd-height rows).
      className={cn(
        'hidden opacity-0 will-change-[opacity] md:flex md:items-center',
        // Riding inside the scroller it is just another flex item: never squeezed, and the
        // scroller's own gap-2 supplies the space after the last tile.
        stacksHoverCluster ? 'shrink-0' : 'md:ml-1',
        interactive && 'transition-opacity duration-250 group-hover/row:opacity-100',
      )}
    >
      <Tooltip label="Add reaction" placement="top" className="flex">
        <button
          tabIndex={interactive ? null : -1}
          onClick={interactive ? handleToggleEmojibar : undefined}
          data-app-region="none"
          className="relative z-(--stack-messages-sticky-header) flex size-7 cursor-pointer items-center justify-center rounded-full text-black/60 transition-none hover:text-black [&_*]:transition-none [.has-workspace-background-image_&]:text-white/70 [.has-workspace-background-image_&]:hover:text-white"
        >
          <Laugh className="size-4.5" strokeWidth={2} absoluteStrokeWidth />
        </button>
      </Tooltip>
      <Tooltip label="Reply" placement="top" className="flex">
        <button
          tabIndex={interactive ? null : -1}
          onClick={interactive ? handleReplyClick : undefined}
          data-app-region="none"
          aria-label="Reply"
          className="relative z-(--stack-messages-sticky-header) flex size-7 cursor-pointer items-center justify-center rounded-full text-black/60 transition-none hover:text-black [&_*]:transition-none [.has-workspace-background-image_&]:text-white/70 [.has-workspace-background-image_&]:hover:text-white"
        >
          <Reply className="size-4.5" strokeWidth={2} absoluteStrokeWidth />
        </button>
      </Tooltip>
    </div>
  ) : null;

  return (
    <div
      ref={messageRef}
      data-debug-id={message.id}
      data-debug-sort-order={message.sort_order}
      className={`group/row relative flex flex-col ${debugEnabled && message.pseudotype === 'optimistic' ? 'bg-amber-200' : ''}`}
      onContextMenu={handleContextMenu}
    >
      <div
        className={cn(
          'flex w-full flex-col',
          // 1px here + the two rows' py-px = a 3px gap between grouped bubbles
          isGroupedWithPrevious ? 'pt-px' : standalone ? 'pt-0' : 'pt-6',
          isGroupedWithNext && interactive && hasTapbacks && 'pb-4',
        )}
      >
        <div className="relative py-px leading-0">
          {!isGroupedWithPrevious && <MessageHeader message={message} author={author} interactive={interactive} />}

          {/* Avatar positioned at bottom of message group */}
          {!isGroupedWithNext && (
            <MessageAvatar
              message={message}
              author={author}
              interactive={interactive}
              isPlaceholder={isPlaceholder || message.author_pseudotype === 'skeleton'}
            />
          )}

          <div {...(isPlaceholder ? {} : longPressAttrs)} className={cn('relative ml-12', isPlaceholder ? 'block' : 'flex')}>
            <div
              className={cn(
                'flex flex-col',
                hasMultipleDisplayedAttachments && 'min-w-0 flex-1',
              )}
            >
              {/* Attachments */}
              {!isPlaceholder && displayedAttachments?.length > 0 && (
                <div
                  className={cn(
                    'wrap-anywhere',
                    hasMultipleDisplayedAttachments ? 'w-full min-w-0' : 'w-fit max-w-message-content',
                    !message.contains_only_attachments && 'pb-1.5',
                  )}
                  data-app-region="none"
                >
                  {/* @ALLAN this pb-1.5 (above) is the space btw attachments and their message content */}
                  <MessageAttachments
                    attachments={displayedAttachments}
                    shouldShowUnreadMarker={shouldShowUnreadMarker}
                    channelId={message.channel_id}
                    messageId={message.id}
                    messageSortOrder={message.sort_order}
                    onUnreadMarkerDismiss={onUnreadMarkerDismiss}
                    unreadMarkerWorkspaceId={unreadMarkerWorkspaceId}
                    unreadMarkerProfileId={unreadMarkerProfileId}
                    onOpenImageLightbox={onOpenImageLightbox}
                    trailingAccessory={stacksHoverCluster ? hoverCluster : null}
                  />
                </div>
              )}

              <div className="relative w-fit" data-app-region="none">
                {isPlaceholder ? (
                  <Skeleton
                    count={1}
                    // Keep placeholder widths deterministic from the row seed so they don't jitter while visible.
                    width={seededSkeletonWidth({ seed: placeholderSeed, min: 50, max: 448 })}
                    style={{ maxWidth: '100%' }}
                    height={32}
                    borderRadius="16px"
                  />
                ) : (
                  <>
                    {/* Message content bubble */}
                    {!message.contains_only_attachments && (
                      <div className="relative">
                        {isEditedMessage && (
                          <Tooltip
                            label="Edited"
                            placement="top"
                            className="absolute -top-[6px] -right-[5px] z-(--stack-messages-edit-asterisk) size-3"
                          >
                            <EditAsteriskIcon className="pointer-events-none h-full w-full stroke-white text-pink-500" />
                          </Tooltip>
                        )}

                        <div ref={bubbleRef} className={bubbleClassName}>
                          <div ref={contentRef} className="relative">
                            <span className="select-extra">{author?.full_name || 'Unknown'}:</span>
                            {message.reply_to_message_id && (
                              <div
                                onClick={handleJumpToReply}
                                className={cn(
                                  'flex flex-col items-start gap-1 self-stretch rounded-[14px]',
                                  'bg-white px-3 py-2 text-cursed/5',
                                  canJumpToReply && 'cursor-pointer hover:bg-gray-50',
                                )}
                              >
                                <ReplyQuote
                                  replyToMessage={replyToMessage}
                                  replyToAuthor={replyToAuthor}
                                  replyToEntitySnapshots={replyToEntitySnapshots}
                                  isReplyToMissing={isReplyToMissing}
                                />
                              </div>
                            )}
                            <RichdownContent
                              html={message.text}
                              richdownVariant={cn(
                                isOwnMessage ? 'richdown-content--channel-message-mine' : 'richdown-content--channel-message-not-mine',
                                MESSAGE_CONTENT_TYPOGRAPHY_CLASS,
                              )}
                              entitySnapshots={message.entity_snapshots}
                              containsOnlyEntities={message.contains_only_entities}
                              containsOnlyEmojiEntities={message.contains_only_emoji_entities}
                              popoverProfileEntities={interactive}
                              truncated
                            />
                            {message.flags?.includes('via_agent') && (
                              <div className={`flex gap-1 pt-3 pb-2 text-2xs font-medium ${message.reply_to_message_id ? 'ps-2.5' : ''}`}>
                                <span
                                  className={`select-none ${
                                    message.contains_only_emoji_entities || !isOwnMessage ? 'text-gray-600' : 'text-white/50'
                                  }`}
                                >
                                  Sent via Agent
                                </span>
                              </div>
                            )}
                          </div>

                          {isContentOverflowing && interactive && (
                            <div className="@container pointer-events-none absolute inset-0">
                              <button
                                type="button"
                                onClick={handleOpenFullMessageClick}
                                className={`pointer-events-auto absolute bottom-2 left-2 z-10 flex size-8 cursor-pointer items-center justify-center rounded-full text-sm font-medium text-white @[8em]:bottom-4 @[8em]:left-4 @[8em]:size-auto @[8em]:px-3 @[8em]:py-2.5 ${
                                  isOwnMessage ? 'bg-gray-700 hover:bg-gray-800' : 'bg-neutral-950/80 hover:bg-neutral-900'
                                }`}
                              >
                                <ReadMoreDotsIcon className="@[8em]:hidden" />
                                <span className="hidden @[8em]:inline">Read more</span>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
                {shouldShowUnreadMarker && !isPlaceholder && !(message.attachments?.length > 0) && (
                  <UnreadMarker
                    workspaceId={unreadMarkerWorkspaceId}
                    channelId={message.channel_id}
                    profileId={unreadMarkerProfileId}
                    onDismiss={onUnreadMarkerDismiss}
                  />
                )}
              </div>

              {!isPlaceholder && visibleLinkPreviews.length > 0 && (
                <div className="flex flex-col gap-1.5 pt-1">{visibleLinkPreviews.map(renderLinkPreview)}</div>
              )}
            </div>

            {!stacksHoverCluster && hoverCluster}

            {isEmojibarOpen && (
              <FloatingPortal>
                <div
                  ref={emojibarRefs.setFloating}
                  style={emojibarFloatingStyles}
                  className="z-(--stack-emoji-picker)"
                  onContextMenu={handleEmojibarContextMenu}
                >
                  <div ref={emojibarContentRef}>
                    <EmojibarPopover onEmojiSelect={handleEmojiSelect} allowGifs={false} />
                  </div>
                </div>
              </FloatingPortal>
            )}
          </div>

          {/* Reactions (stickers) - filter out null emoji (can occur in delete preview) */}
          {stickers.length > 0 && (
            <div className="pointer-events-none absolute inset-0 z-(--stack-message-reactions-stickers)">
              {stickers
                .filter((reaction) => reaction.emoji)
                .map((reaction) => {
                  return (
                    <div
                      key={reaction.id}
                      className="pointer-events-none absolute size-4 origin-center -translate-x-1/2 -translate-y-1/2 bg-contain bg-center bg-no-repeat"
                      style={{
                        top: `${reaction.pos_y}%`,
                        left: `${reaction.pos_x}%`,
                        rotate: `${reaction.angle}deg`,
                        scale: `${reaction.scale * 0.01}`,
                        backgroundImage: `url(${reaction.emoji.public_url})`,
                      }}
                    />
                  );
                })}
            </div>
          )}
        </div>

        {/* Reactions (tapbacks) */}
        {interactive && hasTapbacks && (
          <div
            className={cn(
              'ml-11 flex max-w-158 flex-wrap items-center pt-1',
              shouldHideTapbackRow ? 'pointer-events-none opacity-0' : 'opacity-100',
            )}
          >
            {tapbacks.map((reaction) => {
              return (
                <TapbackReaction
                  key={reaction.id}
                  reaction={reaction}
                  interactive={interactive}
                  onReactionClick={handleReactionClick}
                  canCreateReactions={canCreateReactions}
                />
              );
            })}
            {interactive && canCreateReactions && (
              <div className="relative -ml-0.5 inline-flex size-9 origin-center items-center justify-center">
                <Tooltip label="Add reaction" placement="top" className="flex">
                  <button
                    data-app-region="none"
                    className="flex size-7 cursor-pointer items-center justify-center rounded-full text-black/60 transition-none hover:text-black [&_*]:transition-none [.has-workspace-background-image_&]:text-white/70 [.has-workspace-background-image_&]:hover:text-white"
                    onClick={handleTapbackAddReactionClick}
                    aria-label="Add reaction"
                  >
                    <Laugh className="size-4.5" strokeWidth={2} absoluteStrokeWidth />
                  </button>
                </Tooltip>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Context Menu Portal */}
      <ContextMenuPortal
        isOpen={showContextMenu}
        onClose={handleCloseContextMenu}
        position={contextMenuPosition}
        message={message}
        onAddReaction={handleOpenReactionPicker}
        clickedAttachment={clickedAttachment}
      />
    </div>
  );
};

export default Message;
